import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to export a large Wordpress site to Markdown",
  "description": "Migrating from Wordpress to Markdown sounds easy. Mention it to any developer and they'll say \"Pfft, an afternoon of work at worst\". Suddenly it's 6months later and you're losing your mind.",
  "published": "2020-08-31T00:00:00.000Z",
  "hero": "./img/screenshot-1598892501072.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`This is a technical followup to `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/lessons-from-migrating-a-14-year-old-blog-with-1500-posts-to-gatsby/"
      }}>{`Lessons from migrating a 14 year old blog with 1500 posts to Gatsby`}</a>{`.`}</p>
    <p>{`Migrating from Wordpress to Markdown sounds easy. Mention it to any developer and they'll say `}<em parentName="p">{`"Pfft, an afternoon of work at worst"`}</em></p>
    <ul>
      <li parentName="ul">{`take a Wordpress export from admin tools – 10min`}</li>
      <li parentName="ul">{`find a script that converts to markdown – 20min`}</li>
      <li parentName="ul">{`sip margaritas while the script runs – 1h`}</li>
    </ul>
    <p>{`Suddenly it's 6months later and you're losing your mind.`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/moving-13-years-of-wordpress-blog-to-gatsby-markdown/"
      }}>{`When I started migrating in September 2019`}</a>{`, there were no good scripts. Best I could find was somebody's 7 year old college project – `}<a parentName="p" {...{
        "href": "https://github.com/DengYiping/wordpress-to-markdown"
      }}>{`wordpress-to-markdown`}</a>{`.`}</p>
    <p>{`Complete with bugs, old JavaScript, and gnarly edge cases on my humongous site. Your site accumulates lots of cruft in 14 years 😅`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`A script that converts Wordpress dumps into clean Markdown may have been the dumbest project I ever took on. Sooooo many edge cases 😅 `}<a parentName="p" {...{
          "href": "https://t.co/z8dPUMrBGk"
        }}>{`pic.twitter.com/z8dPUMrBGk`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1298308910072307713"
      }}>{`August 25, 2020`}</a></blockquote>
    <p>{`Nowadays you have `}<a parentName="p" {...{
        "href": "https://github.com/lonekorean/wordpress-export-to-markdown"
      }}>{`wordpress-export-to-markdown`}</a>{` from @lonekorean. Works better and is easier to use.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Dude, did you not come across this? `}<a parentName="p" {...{
          "href": "https://t.co/ZguvarKuyZ"
        }}>{`https://t.co/ZguvarKuyZ`}</a></p>{`— Kyle Shevlin (@kyleshevlin) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/kyleshevlin/status/1298309307587424256"
      }}>{`August 25, 2020`}</a></blockquote>
    <p>{`But the output isn't what I wanted. Great for simple cases, doesn't deal with all the edge cases on a large technical site.`}</p>
    <h2 {...{
      "id": "the-challenge"
    }}>{`The challenge`}</h2>
    <p>{`The core challenge is two-fold: The easy part and the hard part.`}</p>
    <p><strong parentName="p">{`The easy part`}</strong>{` is that Worpdress outputs valid XML that you have to parse. Tons of good libraries for this, problem solved.`}</p>
    <p>{`You also want to download images.`}</p>
    <p>{`Wordpress likes to use linked `}<inlineCode parentName="p">{`<img>`}</inlineCode>{` tags. Sometimes 3rd party, sometimes part of the site.`}</p>
    <p>{`Gatsby, NextJS, Hugo, and other Markdown-based site generators prefer that you keep images part of your source code. Lets you do fun transformations, hosting via CDN, and ensures you don't lose your images.`}</p>
    <p>{`I lost many images to `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Link_rot"
      }}>{`link rot`}</a>{` ☹️`}</p>
    <p><strong parentName="p">{`The hard part`}</strong>{` is that Wordpress HTML is not valid HTML.`}</p>
    <p>{`And that's where the fun begins.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media2.giphy.com/media/8P7y2ARUPFpNaaWEZ3/giphy-loop.mp4?cid=7cf28b0fmwj253y61r0w4w4tul3c7je4dicpzr3c2ur0kgyk&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "the-basic-setup"
    }}>{`The basic setup`}</h2>
    <p>{`You're looking for a script that builds a processing pipeline:`}</p>
    <ol>
      <li parentName="ol">{`Parse XML`}</li>
      <li parentName="ol">{`Iterate through posts`}</li>
      <li parentName="ol">{`Create a `}<inlineCode parentName="li">{`/out/<slug>`}</inlineCode>{` directory`}</li>
      <li parentName="ol">{`Create a `}<inlineCode parentName="li">{`/out/<slug>/img`}</inlineCode>{` directory for images`}</li>
      <li parentName="ol">{`Extract metadata into Markdown frontmatter`}</li>
      <li parentName="ol">{`Download images into the `}<inlineCode parentName="li">{`/img`}</inlineCode>{` directory`}</li>
      <li parentName="ol">{`Hacks to convert Wordpress HTML into almost valid HTML`}</li>
      <li parentName="ol">{`Parse said HTML into a Markdown Abstract Syntax Tree (AST)`}</li>
      <li parentName="ol">{`Fix edge cases in your AST`}</li>
      <li parentName="ol">{`Output clean Markdown with frontmatter into `}<inlineCode parentName="li">{`/out/<slug>/index.md`}</inlineCode></li>
    </ol>
    <p>{`You can see this setup in my `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/wordpress-to-markdown"
      }}>{`wordpress-to-markdown script`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function processExport(file) {
  const parser = new xml2js.Parser()

  fs.readFile(file, function (err, data) {
    if (err) {
      return console.log("Error: " + err)
    }

    parser.parseString(data, function (err, result) {
      if (err) {
        return console.log("Error parsing xml: " + err)
      }
      console.log("Parsed XML")

      const posts = result.rss.channel[0].item

      fs.mkdir("out", function () {
        posts
          .filter((p) => p["wp:post_type"][0] === "post")
          .forEach(processPost)
      })
    })
  })
}
`}</code></pre>
    <p>{`Parses the XML, iterates through `}<inlineCode parentName="p">{`<item>`}</inlineCode>{` entries, and runs `}<inlineCode parentName="p">{`processPost`}</inlineCode>{` on each.`}</p>
    <h3 {...{
      "id": "metadata-into-frontmatter"
    }}>{`Metadata into Frontmatter`}</h3>
    <p>{`I wanted to create full frontmatter without manual edits. That means:`}</p>
    <pre><code parentName="pre" {...{}}>{`    ---
    title: 'Always put side effects last'
    description: ""
    published: 2018-01-10
    redirect_from:
                - /blog/always-put-side-effects-last/swizec/8057
    categories: "Startups, Technical"
    hero: ./img/wp-content-uploads-2016-10-salesforce-tower-panorama-1024x358.jpg
    ---
`}</code></pre>
    <p>{`Title from post title, description based on meta data, a publish date, keep old URL for redirects, combine categories and tags into categories, find a good hero/social image.`}</p>
    <p>{`Data comes from digging around Wordpress exports and figuring out what fits.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const postTitle = typeof post.title === "string" ? post.title : post.title[0]
console.log("Post title: " + postTitle)
const postDate = isFinite(new Date(post.pubDate))
  ? new Date(post.pubDate)
  : new Date(post["wp:post_date"])
console.log("Post Date: " + postDate)
let postData = post["content:encoded"][0]
console.log("Post length: " + postData.length + " bytes")
const slug = slugify(postTitle, {
  remove: /[^\\w\\s]/g,
})
  .toLowerCase()
  .replace(/\\*/g, "")
console.log("Post slug: " + slug)

// takes the longest description candidate
const description = [
  post.description,
  ...post["wp:postmeta"].filter(
    (meta) =>
      meta["wp:meta_key"][0].includes("metadesc") ||
      meta["wp:meta_key"][0].includes("description")
  ),
].sort((a, b) => b.length - a.length)[0]

// Merge categories and tags into tags
const categories = post.category && post.category.map((cat) => cat["_"])
`}</code></pre>
    <p>{`Despite this, you'll notice lots of empty descriptions. Folks get lazy and don't write custom descriptions because Wordpress can guess from the article. I know I did 😇`}</p>
    <p>{`Should I add that guess-work or write 1500 descriptions by hand 🤔`}</p>
    <p>{`Finding the hero image is a matter of processing all images in your article and picking the first.`}</p>
    <p>{`Initial candidates come from your meta data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const heroURLs = post["wp:postmeta"]
  .filter(
    (meta) =>
      meta["wp:meta_key"][0].includes("opengraph-image") ||
      meta["wp:meta_key"][0].includes("twitter-image")
  )
  .map((meta) => meta["wp:meta_value"][0])
  .filter((url) => url.startsWith("http"))
`}</code></pre>
    <p>{`The rest come from your article body.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let images = []
if (heroURLs.length > 0) {
  const url = heroURLs[0]
  ;[postData, images] = await processImage({
    url,
    postData,
    images,
    directory,
  })
}

// downloads images, changes each URL in article
;[postData, images] = await processImages({ postData, directory })

// finds first non-gif image
heroImage = images.find((img) => !img.endsWith("gif"))
`}</code></pre>
    <p>{`From all this metadata, frontmatter comes together with a bit of string concatenation.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let frontmatter
try {
  frontmatter = [
    "---",
    \`title: '\${postTitle.replace(/'/g, "''")}'\`,
    \`description: "\${description}"\`,
    \`published: \${format(postDate, "yyyy-MM-dd")}\`,
    \`redirect_from: 
        - \${redirect_from}\`,
  ]
} catch (e) {
  console.log("----------- BAD TIME", postTitle, postDate)
  throw e
}

if (categories && categories.length > 0) {
  frontmatter.push(\`categories: "\${categories.join(", ")}"\`)
}

frontmatter.push(\`hero: \${heroImage || "../../../defaultHero.jpg"}\`)
frontmatter.push("---")
frontmatter.push("")
`}</code></pre>
    <p>{`Okay that's the easy part.`}</p>
    <h2 {...{
      "id": "converting-to-markdown-and-fixing-edge-cases"
    }}>{`Converting to Markdown and fixing edge cases`}</h2>
    <p>{`Converting Wordpress's invalid HTML to Markdown is the fun part. Edge cases make it even better.`}</p>
    <p>{`You can choose 2 paths here:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/domchristie/turndown"
        }}>{`turndown`}</a>{`, which is a solid HTML to Markdown converter that I didn't know about when doing this`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://unifiedjs.com/"
        }}>{`UnifiedJS`}</a>{`, which is a suite of tools for manipulating ASTs used by a lot of popular libraries`}</li>
    </ul>
    <p>{`I went with Unified.`}</p>
    <p>{`Core setup looks like a pipeline of plugins. You start with an input string, parse it as HTML, turn it into Markdown, output as text.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const markdown = await new Promise((resolve, reject) => {
  unified()
    .use(parseHTML, {
      fragment: true,
      emitParseErrors: true,
      duplicateAttribute: false,
    })
    .use(fixCodeBlocks) // edge case
    .use(fixEmbeds) // edge case
    .use(rehype2remark)
    .use(cleanupShortcodes) // edge-ish case
    .use(stringify, {
      fences: true,
      listItemIndent: 1,
      gfm: false,
      pedantic: false,
    })
    .process(fixBadHTML(postData), (err, markdown) => {
      if (err) {
        reject(err)
      } else {
        let content = markdown.contents
        // edge case
        content = content.replace(/(?<=https?:\\/\\/.*)\\\\_(?=.*\\n)/g, "_")
        // prettify
        resolve(prettier.format(content, { parser: "mdx" }))
      }
    })
})
`}</code></pre>
    <h3 {...{
      "id": "edge-case-1-make-your-html-parseable"
    }}>{`Edge case 1: Make your HTML parseable`}</h3>
    <p>{`Wordpress HTML is pretty good. Plop it in an HTML parser and, like, it won't choke ... but it won't parse correctly either.`}</p>
    <p>{`You'll need to change double newlines to paragraph breaks. Wordpress doesn't wrap paragraphs in `}<inlineCode parentName="p">{`<p></p>`}</inlineCode>{` tags`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function fixBadHTML(html) {
  html = html.replace(/(\\r?\\n){2}/g, "<p></p>")
  return html
}
`}</code></pre>
    <p>{`Yep, Regex for HTML fixing. Find double newlines, replace with empty paragraphs.`}</p>
    <h3 {...{
      "id": "edge-case-2-bad-code-blocks"
    }}>{`Edge case 2: Bad code blocks`}</h3>
    <p>{`I wrote about fixing bad code blocks in my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/you-though-computer-science-has-no-place-in-webdev-heres-a-fun-coding-challenge/"
      }}>{`You though computer science has no place in webdev? Here's a fun coding challenge`}</a>{` article.`}</p>
    <p>{`Your challenge is that this isn't valid HTML:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<pre lang="javascript">
class ReportSize extends React.Component {
  refCallback = element => {
    if (element) {
      this.props.getSize(element.getBoundingClientRect());
    }
  };

  render() {
    return (
      <div ref={this.refCallback} style={{ border: "1px solid red" }}>
        {faker.lorem.paragraphs(Math.random() * 10)}
      </div>
    );
  }
}
</pre>
`}</code></pre>
    <p>{`JSX tags get parsed as HTML and break your code block. You want them to include a `}<inlineCode parentName="p">{`<code></code>`}</inlineCode>{` tag as well. Otherwise Markdown stringifying doesn't work right.`}</p>
    <p>{`Fixing this is tricky and I won't share the full code here. You can see it in `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/wordpress-to-markdown/blob/master/articleCleanup.js#L77"
      }}>{`articleCleanup.js line 77`}</a>{`. All 139 lines of it 🤘`}</p>
    <p>{`The process goes like this:`}</p>
    <ol>
      <li parentName="ol">{`Find code blocks`}</li>
      <li parentName="ol">{`Grab language definition`}</li>
      <li parentName="ol">{`Replace children with a `}<inlineCode parentName="li">{`<code>`}</inlineCode>{` element`}</li>
      <li parentName="ol">{`Fix JSX object props in child nodes`}</li>
      <li parentName="ol">{`Stringify block into HTML`}</li>
      <li parentName="ol">{`Clean HTML with gnarly regex buffoonery`}</li>
      <li parentName="ol">{`Run result through Prettier`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`for (let block of codeBlocks) {
  const lang = block.properties && block.properties.lang

  block.children = [
    {
      type: "element",
      tagName: "code",
      properties: {
        className: lang ? [\`language-\${lang}\`] : null,
      },
      children: [
        {
          type: "text",
          value: cleanBlockHTML(
            toHTML(fixJsxObjectProps(block), settings),
            block.properties && block.properties.lang
          ),
        },
      ],
    },
  ]
}
`}</code></pre>
    <h3 {...{
      "id": "edge-case-3-fixing-embeds"
    }}>{`Edge case 3: Fixing embeds`}</h3>
    <p>{`Lots of ways to embed 3rd party content on a wordpress site. You can use plain old links pasted on their own line, shortcodes, and full HTML embeds.`}</p>
    <p>{`Markdown site generators like to use plain links.`}</p>
    <p>{`You want to change code like:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<blockquote class="twitter-tweet">
  <p lang="en" dir="ltr">
    A script that converts Wordpress dumps into clean Markdown may have been the
    dumbest project I ever took on. Sooooo many edge cases 😅
    <a href="https://t.co/z8dPUMrBGk">pic.twitter.com/z8dPUMrBGk</a>
  </p>
  &mdash; Swizec Teller (@Swizec)
  <a
    href="https://twitter.com/Swizec/status/1298308910072307713?ref_src=twsrc%5Etfw"
    >August 25, 2020</a
  >
</blockquote>
<script
  async
  src="https://platform.twitter.com/widgets.js"
  charset="utf-8"
></script>
`}</code></pre>
    <p>{`Into Markdown that's a link:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`https://twitter.com/Swizec/status/1298308910072307713
`}</code></pre>
    <p>{`Site generator can take this and turn it into an embed. When it starts as a blockquote, you'll have trouble.`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/wordpress-to-markdown/blob/master/articleCleanup.js#L220"
      }}>{`Another 106 lines of code`}</a>{` that I won't share here.`}</p>
    <p>{`Basic idea is that:`}</p>
    <ol>
      <li parentName="ol">{`You find all blockquote nodes`}</li>
      <li parentName="ol">{`All iframe nodes`}</li>
      <li parentName="ol">{`All paragraph nodes`}</li>
      <li parentName="ol">{`Filter for potential embeds`}</li>
      <li parentName="ol">{`Fix the AST for each embed you want to support`}</li>
    </ol>
    <p>{`Taking Twitter as an example, you get this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function fixEmbeds() {
  function isTweet(blockquote) {
    return (
      blockquote.properties &&
      blockquote.properties.className &&
      blockquote.properties.className.includes("twitter-tweet")
    )
  }

  return (tree) => {
    const blockquotes = findRehypeNodes(tree, "blockquote")

    for (let blockquote of blockquotes) {
      if (isTweet(blockquote)) {
        const link = findRehypeNodes(blockquote, "a").pop()
        blockquote.type = "element"
        blockquote.tagName = "p"
        blockquote.children = [{ type: "text", value: link.properties.href }]
      }
    }

    return tree
  }
}
`}</code></pre>
    <h3 {...{
      "id": "edge-case-4-fixing-shortcodes"
    }}>{`Edge case 4: Fixing shortcodes`}</h3>
    <p>{`Shortcodes are a semi-standard system of snippets. Denoted by `}<inlineCode parentName="p">{`[]`}</inlineCode>{` they give CMS users the ability to go beyond writing text.`}</p>
    <p>{`These were popular on internet forums of the late 2000's. Wordpress supports them to this day. Don't know about others.`}</p>
    <p>{`I wanted to get rid of most and preserve any embeds.`}</p>
    <p>{`You can identify an embed because it's a closed shortcode prefixed with the name of a service followed by a link.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`[tweet https://twitter.com/Swizec/status/1298308910072307713]
`}</code></pre>
    <p>{`The gnarly ones are Wordpress's almost-html shortcodes. Big issue on my site were the `}<inlineCode parentName="p">{`[caption][/caption]`}</inlineCode>{` shortcodes.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`[caption id="" align="alignnone" width="560"]<img
  class=" "
  title="Spirograph"
  src="http://upload.wikimedia.org/wikipedia/commons/thumb/9/97/Spirograph3.jpg/800px-Spirograph3.jpg"
  alt="Spirograph"
  width="560"
  height="420"
/>
<a
  class="zem_slink"
  title="Spirograph"
  href="http://en.wikipedia.org/wiki/Spirograph"
  rel="wikipedia"
  target="_blank"
  >Spirograph</a
>[/caption]
`}</code></pre>
    <p>{`It's a shortcode tag with an image and a link. You want to get a clean Markdown image out of this. 🤨`}</p>
    <p>{`You fix this mess by:`}</p>
    <ol>
      <li parentName="ol">{`Finding all paragraphs`}</li>
      <li parentName="ol">{`Seeing if they contain shortcodes`}</li>
      <li parentName="ol">{`Cleaning it up with Regex`}</li>
    </ol>
    <p>{`Core structure is an AST traversal with a loop over candidates:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function cleanupShortcodes() {
  const shortCodeOpenTag = /\\[\\w+ .*\\]/g
  const shortCodeCloseTag = /\\[\\/\\w+]/g
  const embedShortCode = /\\[\\w+ (https?:\\/\\/.*)\\]/g
  const captionShortCode = /\\[caption.*\\]/g

  return (tree) => {
    visit(tree, "text", (node, index, parent) => {
      if (parent.type === "paragraph" && node.value) {
        // clean it up
      }
    })
  }
}
`}</code></pre>
    <p>{`Inside the loop you then:`}</p>
    <ol>
      <li parentName="ol">{`Turn embed shortcodes into plain URLs with regex`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// preserve embed shortcodes as plain URLs
if (node.value.match(embedShortCode)) {
  node.value = node.value.replace(embedShortCode, "$1")
}
`}</code></pre>
    <ol {...{
      "start": 2
    }}>
      <li parentName="ol">{`Turn `}<inlineCode parentName="li">{`[caption]`}</inlineCode>{` shortcodes into image nodes`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// turn [caption] shortcodes into clean images
if (node.value.match(captionShortCode)) {
  visit(parent, "text", (node) => {
    node.value = ""
  })
  visit(parent, "link", (node) => {
    node.type = "image"
    node.title = node.children[0].title
    node.alt = node.children[0].alt
    node.url = node.children[0].url
    node.children = []
  })
}
`}</code></pre>
    <p>{`This changes the parent paragraph node into an image and deletes all children text nodes.`}</p>
    <ol {...{
      "start": 3
    }}>
      <li parentName="ol">{`Remove other shortcodes`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// remove other shortcodes
node.value = node.value
  .replace(shortCodeOpenTag, "")
  .replace(shortCodeCloseTag, "")
`}</code></pre>
    <p>{`I couldn't find a use for them ✌️`}</p>
    <h3 {...{
      "id": "edge-case-5-underscores-in-links"
    }}>{`Edge case 5: Underscores in links`}</h3>
    <p>{`This one was frustrating. Embed links can include underscores, like when you embed a tweet from `}<inlineCode parentName="p">{`@_developit`}</inlineCode>{`.`}</p>
    <p>{`Markdown stringification escapes underscores because it thinks they're emphasis and doesn't understand that some text nodes are link nodes despite not being links.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-markdown"
      }}>{`https://twitter.com/_developit/status/1300154097170083842
`}</code></pre>
    <p>{`That breaks your embed machinery. 🤪`}</p>
    <p>{`You can fix it with a dirty regex hack:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let content = markdown.contents
content = content.replace(/(?<=https?:\\/\\/.*)\\\\_(?=.*\\n)/g, "_")
`}</code></pre>
    <p>{`The reverse lookup with `}<inlineCode parentName="p">{`(?<=)`}</inlineCode>{` ensures you don't touch escaped underscores anywhere other than links.`}<sup parentName="p" {...{
        "id": "fnref-1"
      }}><a parentName="sup" {...{
          "href": "#fn-1",
          "className": "footnote-ref"
        }}>{`1`}</a></sup></p>
    <h2 {...{
      "id": "the-solution"
    }}>{`The solution`}</h2>
    <p>{`You can use my script 👉 `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/wordpress-to-markdown"
      }}>{`github/Swizec/wordpress-to-markdown`}</a></p>
    <pre><code parentName="pre" {...{}}>{`    $ git clone https://github.com/Swizec/wordpress-to-markdown

    # download your wordpress xml
    # change filename on convert.js line 27

    $ yarn
    $ yarn convert

    # sip margaritas
`}</code></pre>
    <p>{`Deals with every edge case described above, produces clean markdown output. Even runs it through Prettier ✌️`}</p>
    <p>{`PRs welcome.`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p><sup parentName="p" {...{
        "id": "fnref-1"
      }}><a parentName="sup" {...{
          "href": "#fn-1",
          "className": "footnote-ref"
        }}>{`1`}</a></sup>{` the look-behind and look-ahead support in PCRE regexes means they are technically more powerful than regular languages. On the level of pushdown automata I think.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      